import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostBinding,
  HostListener,
  inject,
  input,
  Input,
  signal,
  viewChild,
  ViewChild,
} from '@angular/core'

import { first } from '@app/core/array/first'
import { last } from '@app/core/array/last'
import { ProfileModule } from '@app/modules/profile/profile.module'
import type { ScrollboxComponent } from '@app/modules/ui/components/scrollbox/scrollbox.component'
import { UIModule } from '@app/modules/ui/ui.module'
import { MemberService } from '@app/services/member.service'
import { EmptyConversationComponent } from '@messaging/components/empty-conversation/empty-conversation.component'

import type { Conversation } from '../../models/conversation.model'
import { ConversationUIService } from '../../services/conversation-ui.service'
import { LockedMessageComponent } from '../locked-message/locked-message.component'
import type { MessageComponent } from '../message/message.component'
import { MessageFormComponent } from '../message-form/message-form.component'
import { MessagesComponent } from '../messages/messages.component'
import { ConversationStore } from '../store/conversation.store'

@Component({
  selector: 'sb-conversation-content',
  templateUrl: './conversation-content.component.html',
  styleUrl: './conversation-content.component.scss',
  imports: [
    ProfileModule,
    UIModule,
    LockedMessageComponent,
    MessagesComponent,
    MessageFormComponent,
    EmptyConversationComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationContentComponent {
  readonly conversationStore = inject(ConversationStore)
  readonly conversationUIService = inject(ConversationUIService)
  readonly memberService = inject(MemberService)
  private readonly messagesComponent = viewChild(MessagesComponent)
  private readonly messageFormComponent = viewChild(MessageFormComponent)
  private lastMessageTimestamp: string | undefined
  private firstMessageTimestamp: string | undefined

  @HostBinding('animate.enter') enterAnimate = 'enter'

  @HostListener('animationend')
  onEnterDone() {
    this.showAnimationFinished.set(true)
  }

  @Input({ required: true }) conversation!: Conversation
  readonly readyForPopups = input.required<boolean>()
  readonly showAnimationFinished = signal(false)

  private readonly handlePopups = computed(() => this.readyForPopups() && this.showAnimationFinished())

  @ViewChild('scrollbox') scrollbox: ScrollboxComponent | undefined

  readonly _uiChange = signal(0)

  forceUiChanged(): void {
    this._uiChange.set(this._uiChange() + 1)
  }

  private readonly uiChange = effect(() => {
    this._uiChange()
    const messageComponents = this.messagesComponent()?.messageComponents() ?? []

    this.handleScrollChange(messageComponents)

    if (!this.handlePopups()) {
      return
    }

    this.handleAnchorMessagePopup(messageComponents)

    this.handleAttachPhotoButton()
    this.handleVoiceMessageButton()

    this.handleLastLockedMessagePhoto(messageComponents)
    this.handleLastLockedVoiceMessage(messageComponents)

    this.handleLastViewableVoiceMessage(messageComponents)
  })

  private handleExpiredMessage(messageComponent: MessageComponent): void {
    const { message, messagePhotoComponent, voiceMessageComponent } = messageComponent

    if (message.type === 'photo' && messagePhotoComponent) {
      this.conversationUIService.openExpiredPhotoModal(messagePhotoComponent.elementRef.nativeElement)
    } else if (message.type === 'voice' && voiceMessageComponent) {
      this.conversationUIService.openExpiredVoiceMessageModal(voiceMessageComponent.elementRef.nativeElement)
    }
  }

  private handleScrollChange(messageComponents: readonly MessageComponent[]): void {
    const lastMessageTimestamp = last(messageComponents)?.message.timestamp
    const firstMessageTimestamp = first(messageComponents)?.message.timestamp

    if (lastMessageTimestamp !== this.lastMessageTimestamp) {
      // New messages were appended
      this.scrollbox?.resetScroll()
    } else if (firstMessageTimestamp !== this.firstMessageTimestamp) {
      // Previous messages were prepended
      const previousFirstMessageIndex = messageComponents.findIndex(
        component => component.message.timestamp == this.firstMessageTimestamp,
      )

      const messageComponentToFocus = messageComponents[Math.max(previousFirstMessageIndex - 1, 0)]

      if (messageComponentToFocus) {
        messageComponentToFocus.elementRef.nativeElement.scrollIntoView({
          behavior: 'instant',
          block: 'start',
          inline: 'center',
        })
      }
    }

    this.lastMessageTimestamp = lastMessageTimestamp
    this.firstMessageTimestamp = firstMessageTimestamp
  }

  private handleAnchorMessagePopup(messageComponents: readonly MessageComponent[]): void {
    const anchorMessageComponent = messageComponents.find(messageComponent =>
      this.conversationStore.isAnchor(messageComponent.message),
    )

    if (!anchorMessageComponent) {
      return
    }

    if (
      (anchorMessageComponent.message.type === 'photo' || anchorMessageComponent.message.type === 'voice') &&
      anchorMessageComponent.message.status === 'expired'
    ) {
      this.handleExpiredMessage(anchorMessageComponent)
    } else {
      this.conversationUIService.openAnchorMessageHighlighter(anchorMessageComponent.elementRef.nativeElement)
    }
  }

  private handleAttachPhotoButton(): void {
    const attachPhotoButton = this.messageFormComponent()?.attachPhotoButton()?.nativeElement

    if (attachPhotoButton) {
      this.conversationUIService.openMessagePhotoAttachExplainer(attachPhotoButton)
    }
  }

  private handleVoiceMessageButton(): void {
    const voiceMessageButton = this.messageFormComponent()?.fieldMessage()?.voiceMessageButton()?.nativeElement

    if (voiceMessageButton) {
      this.conversationUIService.openVoiceMessageExplainer(voiceMessageButton)
    }
  }

  private handleLastLockedMessagePhoto(messageComponents: readonly MessageComponent[]): void {
    const lastLockedMessagePhoto = [...messageComponents]
      .reverse()
      .find(({ message }) => message.type === 'photo' && message.status === 'locked')?.messagePhotoComponent

    if (lastLockedMessagePhoto) {
      this.conversationUIService.openMessagePhotoUnlockExplainer(lastLockedMessagePhoto.elementRef.nativeElement)
    }
  }

  private handleLastLockedVoiceMessage(messageComponents: readonly MessageComponent[]): void {
    const lastLockedVoiceMessage = [...messageComponents]
      .reverse()
      .find(({ message }) => message.type === 'voice' && message.status === 'locked')?.voiceMessageComponent

    if (lastLockedVoiceMessage) {
      this.conversationUIService.openVoiceMessageUnlockExplainer(lastLockedVoiceMessage.elementRef.nativeElement)
    }
  }

  private handleLastViewableVoiceMessage(messageComponents: readonly MessageComponent[]): void {
    const lastViewableVoiceMessage = [...messageComponents]
      .reverse()
      .find(
        ({ message }) => message.type === 'voice' && message.status === 'viewable' && !message.is_me,
      )?.voiceMessageComponent

    if (lastViewableVoiceMessage) {
      this.conversationUIService.openVoiceMessagePlayExplainer(lastViewableVoiceMessage.elementRef.nativeElement)
    }
  }

  onMessageChanged(): void {
    this._uiChange.update(v => v + 1)
  }
}
